import { ReactNode } from "react";
import { create } from "zustand";

export type ToastType = "success" | "error" | "info";

export type ToastAction = {
    label: string;
    onClick: () => void;
};

export type ToastOptions = {
    type?: ToastType;
    action?: ToastAction;
    secondaryAction?: ToastAction;
    persistent?: boolean;
    delay?: number;
    duration?: number;
};

export type Toast = {
    id: number;
    message: ReactNode;
    type: ToastType;
    action?: ToastAction;
    secondaryAction?: ToastAction;
    persistent?: boolean;
    duration?: number;
};

type StateStoreToast = {
    toasts: Array<Toast>;
};

type StoreToastActions = {
    show: (message: ReactNode, typeOrOptions?: ToastType | ToastOptions, action?: ToastAction, delay?: number) => number;
    remove: (id: number) => void;
};

let nextId = 0;

const useStoreToast = create<StateStoreToast & StoreToastActions>((set) => ({
    toasts: [],

    show: (message: ReactNode, typeOrOptions: ToastType | ToastOptions = "info", action?: ToastAction, delay?: number) => {
        const options: ToastOptions = typeof typeOrOptions === "string"
            ? { type: typeOrOptions, action, delay }
            : typeOrOptions;

        const id = nextId++;
        const toast: Toast = {
            id,
            message,
            type: options.type ?? "info",
            action: options.action,
            secondaryAction: options.secondaryAction,
            persistent: options.persistent,
            duration: options.duration,
        };

        const add = () => set((state) => ({ toasts: [...state.toasts, toast] }));

        if(options.delay) {
            setTimeout(add, options.delay);
        } else {
            add();
        }

        return id;
    },

    remove: (id: number) => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
    },
}));

export default useStoreToast;
